/* eslint-disable @typescript-eslint/ban-ts-comment */
import {
  GetCustomerPypeStreamsLiveQuery,
  GetCustomerPypeStreamsSandboxQuery,
  PostCustomersPypesLiveMutation,
  PostCustomersPypesSandboxMutation,
  proStudioUrqlGql as urqlGql,
} from '@pypestream/api-services';
import { throwErrorWithId } from '@pypestream/utils';

import { ProStudioEvents, ProStudioContext } from '../prostudio.xstate.types';
import { Pype } from '../prostudio.api-service.types';
import { checkForErrors } from '../../utils';
import { createPypeChannel, createTwilioVoiceChannel } from './helpers/channel';

export const createChannel = async (
  ctx: ProStudioContext,
  event: ProStudioEvents
): Promise<{ botPypes: Pype[] } | void> => {
  if (event.type !== 'createChannel') {
    return;
  }

  const { project, botName, customerId } = ctx;

  const uniqueCustomerId = project?.projectProStudioSetting?.customerId;

  if (!uniqueCustomerId) {
    throwErrorWithId('Customer ID is required');
    return;
  }

  if (!botName) {
    throwErrorWithId('Bot Name is required');
    return;
  }

  const projectId = project?.id;

  if (!projectId) {
    throwErrorWithId('Project ID is required');
    return;
  }

  if (!customerId) {
    throwErrorWithId('Customer ID is required');
    return;
  }

  const botId = `${customerId}.${botName}`;

  const { env, name: channelName } = event.payload;

  const createPypeMutation = {
    live: urqlGql.postCustomersPypesLive,
    sandbox: urqlGql.postCustomersPypesSandbox,
  };

  const createPype = createPypeMutation[env];

  const pypeRes = await createPype({
    customerId: uniqueCustomerId,
    projectId,
    description: '',
    id: '',
    logo: 'https://s3-us-west-1.amazonaws.com/pypestream-live/assets/avatars_default/pypes/Yellow+on+White.png',
    name: `${botName}-${channelName.toLowerCase().replace(/ /g, '_')}`,
  });

  checkForErrors(pypeRes);

  const pypeData = pypeRes.data;
  const pypeId =
    env === 'live'
      ? (pypeData as PostCustomersPypesLiveMutation)?.pypesPostCustomersPypes
          ?.id
      : (pypeData as PostCustomersPypesSandboxMutation)
          ?.pypesPostCustomersPypesSandbox?.id;

  if (!pypeId) {
    throwErrorWithId('Pype ID is required');
    return;
  }

  const getStreams = {
    live: urqlGql.getCustomerPypeStreamsLive,
    sandbox: urqlGql.getCustomerPypeStreamsSandbox,
  };

  const getStreamsRes = await getStreams[env]({
    projectId,
    customerId: uniqueCustomerId,
    pypeId,
  });

  checkForErrors(getStreamsRes);
  const streamId =
    env === 'live'
      ? (getStreamsRes.data as GetCustomerPypeStreamsLiveQuery)
          ?.pypesGetCustomersPypesStreams?.[0]?.id
      : (getStreamsRes.data as GetCustomerPypeStreamsSandboxQuery)
          ?.pypesGetCustomersPypesStreamsSandbox?.[0]?.id;

  if (!streamId) {
    throwErrorWithId('Stream ID is required');
    return;
  }

  const updateStreamBot =
    env === 'live'
      ? urqlGql.updateStreamBotLive
      : urqlGql.updateStreamBotSandbox;

  const updateStreamBotRes = await updateStreamBot({
    pypeId,
    streamId,
    customerId: uniqueCustomerId,
    startChatBotId: botId,
    endChatBotEnabled: false,
    projectId,
  });

  checkForErrors(updateStreamBotRes);

  const { cb, ...channelProps } = event.payload;

  switch (channelProps.type) {
    case 'twilio_voice':
      return createTwilioVoiceChannel({
        streamId,
        channelProps,
        projectId,
        uniqueCustomerId,
        ctx,
        event,
        botId,
        cb,
      });

    default:
      return createPypeChannel({
        pypeId,
        streamId,
        channelProps,
        projectId,
        cb,
        ctx,
        event,
      });
  }
};
